import { useEffect, useState } from "react";
import {
  type DepartmentDTO,
  type HotelDTO,
  deleteHotel,
  getUsersApi,
  listDepartments,
  listHotels,
  useCore,
} from "@praesto/core";
import {
  Toolbar,
  Card,
  Button,
  EmptyState,
  ConfirmDialog,
  PageFull,
  SkeletonList,
  SkeletonCardListItem,
} from "@praesto/ui-kit";
import { HotelModal } from "./HotelModal";
import { DepartmentModal } from "./DepartmentModal";

export function StructureView() {
  const core = useCore();
  const [hotels, setHotels] = useState<ReadonlyArray<HotelDTO>>([]);
  const [departments, setDepartments] = useState<Record<string, ReadonlyArray<DepartmentDTO>>>({});
  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});
  const [showNew, setShowNew] = useState(false);
  const [editHotel, setEditHotel] = useState<HotelDTO | null>(null);
  const [deptHotelId, setDeptHotelId] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Hotel id pending a disable confirmation (null = no dialog).
  const [confirmDisableId, setConfirmDisableId] = useState<string | null>(null);
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const r = await listHotels(api.apiBase, tok);
      if (cancelled) return;
      setHotels(r.hotels);
      // Departments and member counts load in parallel across all hotels.
      const [deptPairs, countPairs] = await Promise.all([
        Promise.all(
          r.hotels.map(async (h) => {
            const d = await listDepartments(api.apiBase, h.id, tok);
            return [h.id, d.departments] as const;
          }),
        ),
        Promise.all(
          r.hotels.map(async (h) => {
            const ms = await core.organization.membershipsForHotel(h.id);
            return [h.id, ms.length] as const;
          }),
        ),
      ]);
      if (cancelled) return;
      setDepartments(Object.fromEntries(deptPairs));
      setMemberCounts(Object.fromEntries(countPairs));
    })()
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [reload, core]);

  async function confirmedDisable() {
    const id = confirmDisableId;
    if (!id) return;
    setDisabling(true);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      // Confirming forces the disable even when active members exist.
      await deleteHotel(api.apiBase, id, { force: true }, tok);
      setReload((r) => r + 1);
      setConfirmDisableId(null);
    } catch (e) {
      setError(String(e));
    } finally {
      setDisabling(false);
    }
  }

  return (
    <PageFull>
      <Toolbar
        title="Struttura"
        actions={
          <Button
            size="md"
            variant="primary"
            className="w-full sm:w-auto"
            onClick={() => setShowNew(true)}
          >
            Nuovo hotel
          </Button>
        }
      />
      {error && error.includes("403") ? (
        <EmptyState
          title="Accesso non autorizzato"
          description="Non hai i permessi per gestire la struttura: chiedi al titolare dell'account."
        />
      ) : error ? (
        <p className="text-sm text-danger mb-3">{error}</p>
      ) : null}
      {loading && !error ? (
        <SkeletonList count={3} item={<SkeletonCardListItem />} />
      ) : hotels.length === 0 && !error ? (
        <EmptyState
          title="Nessun hotel"
          description="Crea il primo hotel per iniziare a strutturare la tua organizzazione."
          action={
            <Button size="sm" variant="primary" onClick={() => setShowNew(true)}>
              Nuovo hotel
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {hotels.map((h) => (
            <Card key={h.id} padding="sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-fg-default">
                    {h.name}
                    {h.status === "disabled" && (
                      <span className="ml-2 text-caption text-fg-muted">(disabilitato)</span>
                    )}
                  </div>
                  <div className="text-xs text-fg-muted mt-0.5">
                    {h.city ? `${h.city} · ` : ""}
                    <span className="whitespace-nowrap">
                      {(departments[h.id] ?? []).length} reparti
                    </span>{" "}
                    ·{" "}
                    <span className="whitespace-nowrap">
                      {memberCounts[h.id] ?? 0}{" "}
                      {(memberCounts[h.id] ?? 0) === 1 ? "persona" : "persone"}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 sm:flex-nowrap sm:flex-shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => setEditHotel(h)}>
                    Modifica
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeptHotelId(h.id)}>
                    Reparti
                  </Button>
                  {h.status === "active" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmDisableId(h.id)}
                      className="text-danger"
                    >
                      Disabilita
                    </Button>
                  )}
                </div>
              </div>
              {(departments[h.id] ?? []).length > 0 && (
                <ul className="mt-2 text-xs text-fg-muted space-y-0.5">
                  {(departments[h.id] ?? []).map((d) => (
                    <li key={d.code}>
                      • {d.displayName} ({d.code})
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ))}
        </div>
      )}
      {showNew && (
        <HotelModal
          onClose={() => setShowNew(false)}
          onDone={() => {
            setShowNew(false);
            setReload((r) => r + 1);
          }}
        />
      )}
      {editHotel && (
        <HotelModal
          initial={editHotel}
          onClose={() => setEditHotel(null)}
          onDone={() => {
            setEditHotel(null);
            setReload((r) => r + 1);
          }}
        />
      )}
      {deptHotelId && (
        <DepartmentModal
          hotelId={deptHotelId}
          onClose={() => {
            setDeptHotelId(null);
            // Refresh so the structure list's "N dipartimenti" count updates.
            setReload((r) => r + 1);
          }}
        />
      )}
      <ConfirmDialog
        open={confirmDisableId !== null}
        title="Disabilitare l'hotel?"
        message="L'hotel sparisce dalle strutture attive anche se ha persone assegnate: turni e checklist non saranno più pianificabili qui finché non lo riattivi."
        confirmLabel="Disabilita"
        cancelLabel="Annulla"
        tone="danger"
        busy={disabling}
        onConfirm={() => void confirmedDisable()}
        onCancel={() => setConfirmDisableId(null)}
      />
    </PageFull>
  );
}
